import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Map, MessageSquare, FileText, Book, Layout, Search, ChevronRight } from 'lucide-react';
import 'reactflow/dist/style.css';
import { LoginDialog } from './components/LoginDialog';
import { useState, useEffect } from 'react';
import { auth } from './firebase/config';
import CustomMindmapViewer from './components/CustomMindmapViewer';


const NavigationBar = ({ onGetStarted }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
        <nav className="fixed top-0 left-0 right-0 bg-black/80 backdrop-blur-xl z-50 border-b border-purple-900/20">
            <div className="max-w-6xl mx-auto px-6">
                <div className="flex justify-between items-center h-16">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                            <span className="text-white font-bold">P</span>
                        </div>
                        <span className="text-xl font-bold text-white">Papers with Graph</span>
                    </div>

                    <div className="hidden md:flex items-center gap-8">
                        <a href="#features" className="text-gray-300 hover:text-purple-400 transition-colors">Features</a>
                        <a href="#pricing" className="text-gray-300 hover:text-purple-400 transition-colors">Pricing</a>
                        <button
                            onClick={onGetStarted}
                            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300">
                            Get Started
                        </button>
                    </div>

                    <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
                        {isOpen ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            <div className={`md:hidden ${isOpen ? 'block' : 'hidden'} border-t border-purple-900/20 bg-black`}>
                <div className="px-4 py-2 space-y-1">
                    <a href="#features" className="block px-3 py-2 rounded-md text-gray-300 hover:bg-purple-500/10 hover:text-purple-400">Features</a>
                    <a href="#pricing" className="block px-3 py-2 rounded-md text-gray-300 hover:bg-purple-500/10 hover:text-purple-400">Pricing</a>
                </div>
            </div>
        </nav>
    );
};

const GridBackground = () => (
    <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0A0118] to-black" />

        {/* Combine the radial gradient and dot patterns into one layer */}
        <div
            className="absolute inset-0"
            style={{
                background: `
                    radial-gradient(ellipse at center, rgba(147,51,234,0.03), rgba(0,0,0,0.5)),
                    radial-gradient(circle at center, rgba(147,51,234,0.15) 1px, transparent 1px)
                `,
                backgroundSize: '100% 100%, 40px 40px',
                transform: 'translateZ(0)' // Force GPU acceleration
            }}
        />

        {/* Single glowing orb with reduced blur */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full filter blur-2xl transform-gpu" />
    </div>
);

const FeatureShowcase = () => {
    const [selectedFeature, setSelectedFeature] = useState('mindmap');
    const [isSelectionChatOpen, setIsSelectionChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [currentStreamingText, setCurrentStreamingText] = useState('');
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [userTypingText, setUserTypingText] = useState('');

    // Update the mindmap data with a real paper example
    const mindmapData = {
        id: "root",
        text: "Attention Is All You Need",
        type: "main-topic",
        width: 120,  // Slightly wider to accommodate title
        height: 25,
        children: [
            {
                id: "1",
                text: "Key Contributions",
                type: "subtopic",
                width: 80,
                height: 25,
                children: [
                    {
                        id: "1.1",
                        text: "Transformer Architecture",
                        type: "definition",
                        width: 90,
                        height: 25
                    },
                    {
                        id: "1.2",
                        text: "Self-Attention Mechanism",
                        type: "example",
                        width: 90,
                        height: 25
                    },
                ]
            },
            {
                id: "2",
                text: "Architecture",
                type: "subtopic",
                width: 70,
                height: 25,
                children: [
                    {
                        id: "2.1",
                        text: "Encoder-Decoder",
                        type: "definition",
                        width: 80,
                        height: 25
                    },
                    {
                        id: "2.2",
                        text: "Multi-Head Attention",
                        type: "example",
                        width: 90,
                        height: 25
                    },
                ]
            },
            {
                id: "3",
                text: "Results",
                type: "subtopic",
                width: 60,
                height: 25,
                children: [
                    {
                        id: "3.1",
                        text: "BLEU Score Improvements",
                        type: "definition",
                        width: 100,
                        height: 25
                    },
                    {
                        id: "3.2",
                        text: "Training Efficiency",
                        type: "example",
                        width: 80,
                        height: 25
                    },
                ]
            }
        ]
    };

    // Example paper text with abbreviations and key points
    const examplePaper = {
        abbreviations: [
            { term: "NLP", definition: "Natural Language Processing" },
            { term: "BERT", definition: "Bidirectional Encoder Representations from Transformers" },
            { term: "MLM", definition: "Masked Language Modeling" },
        ],
        keyPoints: [
            "Transformer models revolutionized NLP tasks",
            "Self-attention mechanism enables parallel processing",
            "Pre-training and fine-tuning paradigm improves efficiency",
        ],
        selectedText: "The Transformer architecture revolutionized natural language processing through its self-attention mechanism and parallel processing capabilities."
    };

    // Add these new states and constants
    const sampleQuestions = [
        "What are the key findings of this paper?",
        "Can you explain the methodology?",
        "What are the main conclusions?",
        "Summarize the results section."
    ];

    const sampleResponses = [
        "The key findings reveal that transformer models have revolutionized NLP through their self-attention mechanisms, enabling better understanding of context and relationships between words.",
        "The methodology employs a novel approach using parallel processing and self-attention mechanisms to analyze text data. This allows for more efficient processing of language inputs.",
        "The main conclusions demonstrate that the self-attention mechanism is crucial for understanding context in natural language processing tasks.",
        "The results show significant improvements in processing efficiency and accuracy compared to previous approaches, particularly in tasks involving complex language understanding."
    ];

    // Add this new effect for auto-demonstration
    useEffect(() => {
        if (selectedFeature === 'chat') {
            let currentQuestionIndex = 0;
            let isActive = true;

            const simulateConversation = async () => {
                if (!isActive) return;

                // Type and send question
                const question = sampleQuestions[currentQuestionIndex];
                let typedQuestion = '';
                const input = document.querySelector('input[name="message"]');
                const form = input?.closest('form');

                // Simulate typing
                for (let i = 0; i < question.length && isActive; i++) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                    typedQuestion += question[i];
                    if (input) input.value = typedQuestion;
                }

                await new Promise(resolve => setTimeout(resolve, 500));
                if (!isActive) return;

                // Simulate form submission
                if (form && input) {
                    input.value = question;
                    handleSendMessage(question);
                }

                // Wait for response to complete
                await new Promise(resolve => setTimeout(resolve, 3000));
                if (!isActive) return;

                // Move to next question
                currentQuestionIndex = (currentQuestionIndex + 1) % sampleQuestions.length;

                // Pause before next question
                await new Promise(resolve => setTimeout(resolve, 2000));
                if (!isActive) return;

                // Continue the loop
                simulateConversation();
            };

            simulateConversation();

            return () => {
                isActive = false;
                setChatMessages([]);
            };
        }
    }, [selectedFeature]);

    // Update the simulateStreamingResponse function
    const simulateStreamingResponse = async () => {
        const response = sampleResponses[chatMessages.length % sampleResponses.length];
        setIsTyping(true);
        let currentText = '';

        for (let i = 0; i < response.length; i++) {
            await new Promise(resolve => setTimeout(resolve, 30));
            currentText += response[i];
            setCurrentStreamingText(currentText);
        }

        setIsTyping(false);
        setCurrentStreamingText('');
        setChatMessages(prev => [...prev, { role: 'assistant', content: response }]);
    };

    const handleSendMessage = (message) => {
        setChatMessages(prev => [...prev, { role: 'user', content: message }]);
        simulateStreamingResponse();
    };

    // Update the chat section to include the streaming effect
    const ChatWindow = () => {
        const messagesContainerRef = React.useRef(null);

        const scrollToBottom = () => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
        };

        React.useEffect(() => {
            scrollToBottom();
        }, [chatMessages, currentStreamingText, userTypingText]);

        return (
            <div className="flex flex-col h-[300px] bg-gray-800/50 rounded-lg border border-purple-500/30">
                <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-purple-500/20 scrollbar-track-transparent"
                >
                    {chatMessages.map((message, index) => (
                        <div
                            key={index}
                            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[80%] rounded-lg px-4 py-2 ${message.role === 'user'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-200'
                                    }`}
                            >
                                {message.content}
                            </div>
                        </div>
                    ))}
                    {isUserTyping && (
                        <div className="flex justify-end">
                            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-purple-600 text-white">
                                {userTypingText}
                                <span className="animate-pulse">▊</span>
                            </div>
                        </div>
                    )}
                    {isTyping && (
                        <div className="flex justify-start">
                            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-gray-700 text-gray-200">
                                {currentStreamingText}
                                <span className="animate-pulse">▊</span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-purple-500/30">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            const input = e.target.elements.message;
                            if (input.value.trim()) {
                                setIsUserTyping(false);
                                handleSendMessage(input.value);
                                input.value = '';
                            }
                        }}
                        className="flex gap-2"
                    >
                        <input
                            type="text"
                            name="message"
                            placeholder="Ask about the selection..."
                            className="flex-1 bg-black/50 border border-purple-900/30 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                            onChange={(e) => {
                                setIsUserTyping(true);
                                setUserTypingText(e.target.value);
                            }}
                            onBlur={() => setIsUserTyping(false)}
                            onFocus={(e) => {
                                if (e.target.value) {
                                    setIsUserTyping(true);
                                    setUserTypingText(e.target.value);
                                }
                            }}
                        />
                        <button
                            type="submit"
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                            disabled={isTyping}
                        >
                            Send
                        </button>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <section className="relative px-6 py-24 overflow-hidden">
            <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-center mb-12">
                    <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                        How It Works
                    </span>
                </h2>

                {/* Feature Navigation */}
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                    {[
                        { id: 'mindmap', label: 'Interactive Mindmaps', icon: <Map /> },
                        { id: 'chat', label: 'Smart Selection Chat', icon: <MessageSquare /> },
                        { id: 'keypoints', label: 'Key Points', icon: <FileText /> },
                        { id: 'abbreviations', label: 'Abbreviations', icon: <Book /> },
                    ].map(feature => (
                        <button
                            key={feature.id}
                            onClick={() => setSelectedFeature(feature.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${selectedFeature === feature.id
                                ? 'bg-purple-600 text-white'
                                : 'bg-purple-900/20 text-purple-400 hover:bg-purple-900/30'
                                }`}
                        >
                            {feature.icon}
                            {feature.label}
                        </button>
                    ))}
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-900/10 text-gray-500">
                        <ChevronRight />
                        More
                    </div>
                </div>

                {/* Feature Preview Window */}
                <div className="relative bg-black/50 border border-purple-900/30 rounded-xl overflow-hidden">
                    {/* Window Header */}
                    <div className="bg-black/80 border-b border-purple-900/30 p-4 flex items-center">
                        <div className="flex gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                        </div>
                        <div className="flex-1 text-center text-sm text-gray-400">
                            Research Paper Viewer
                        </div>
                    </div>

                    {/* Feature Content */}
                    <div className="h-[500px] p-6">
                        {selectedFeature === 'mindmap' && (
                            <div className="h-full flex items-center justify-center animate-fade-in">
                                <div className="w-full h-full bg-purple-900/10 rounded-lg border border-purple-900/20 p-6 flex items-center justify-center">
                                    <div className="w-full h-full max-w-3xl max-h-[400px]">
                                        <CustomMindmapViewer
                                            mindmapData={mindmapData}
                                            initialScale={0.6}
                                            initialPosition={{ x: 0, y: 0 }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedFeature === 'chat' && (
                            <div className="h-full flex items-center justify-center relative animate-fade-in">
                                <div className="w-full max-w-3xl bg-purple-900/10 rounded-lg border border-purple-900/20 p-6">
                                    <div className="mb-4">
                                        <p className="text-gray-400 mb-4">Selected text:</p>
                                        <p className="text-gray-300 leading-relaxed bg-purple-900/20 p-4 rounded-lg border border-purple-900/30">
                                            {examplePaper.selectedText}
                                        </p>
                                    </div>
                                    <ChatWindow />
                                </div>
                            </div>
                        )}

                        {selectedFeature === 'keypoints' && (
                            <div className="h-full flex items-center justify-center animate-fade-in">
                                <div className="w-full max-w-3xl bg-purple-900/10 rounded-lg border border-purple-900/20 p-6">
                                    <h3 className="text-xl font-semibold text-purple-400 mb-4">Key Points</h3>
                                    <ul className="space-y-3">
                                        {examplePaper.keyPoints.map((point, index) => (
                                            <li
                                                key={index}
                                                className="flex items-start gap-3 text-gray-300 animate-slide-up"
                                                style={{ animationDelay: `${index * 150}ms` }}
                                            >
                                                <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                                                {point}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {selectedFeature === 'abbreviations' && (
                            <div className="h-full flex items-center justify-center animate-fade-in">
                                <div className="w-full max-w-3xl bg-purple-900/10 rounded-lg border border-purple-900/20 p-6">
                                    <h3 className="text-xl font-semibold text-purple-400 mb-4">Abbreviations</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {examplePaper.abbreviations.map((abbr, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-3 animate-slide-up"
                                                style={{ animationDelay: `${index * 150}ms` }}
                                            >
                                                <span className="px-2 py-1 bg-purple-600/20 rounded text-purple-400 font-medium">
                                                    {abbr.term}
                                                </span>
                                                <span className="text-gray-300">{abbr.definition}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedFeature === 'summary' && (
                            <div className="h-full flex items-center justify-center animate-fade-in">
                                <div className="w-full max-w-3xl bg-purple-900/10 rounded-lg border border-purple-900/20 p-6">
                                    <h3 className="text-xl font-semibold text-purple-400 mb-4">AI-Generated Summary</h3>
                                    <div className="space-y-4">
                                        <div className="bg-purple-900/20 p-4 rounded-lg border border-purple-900/30">
                                            <p className="text-gray-300 leading-relaxed">
                                                The Transformer architecture introduces a novel approach to sequence transduction tasks,
                                                entirely replacing traditional recurrent neural networks with self-attention mechanisms.
                                                This paper presents several key innovations:
                                            </p>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="flex items-start gap-3 text-gray-300 animate-slide-up">
                                                <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                                                Multi-head attention mechanism allowing parallel processing
                                            </div>
                                            <div className="flex items-start gap-3 text-gray-300 animate-slide-up">
                                                <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                                                Position-wise feed-forward networks
                                            </div>
                                            <div className="flex items-start gap-3 text-gray-300 animate-slide-up">
                                                <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                                                Scaled dot-product attention computation
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedFeature === 'mcq' && (
                            <div className="h-full flex items-center justify-center animate-fade-in">
                                <div className="w-full max-w-3xl bg-purple-900/10 rounded-lg border border-purple-900/20 p-6">
                                    <h3 className="text-xl font-semibold text-purple-400 mb-4">Practice Questions</h3>
                                    <div className="space-y-6">
                                        <div className="space-y-4">
                                            <p className="text-gray-300">What is the main innovation introduced in the Transformer architecture?</p>
                                            <div className="space-y-2">
                                                {[
                                                    'Self-attention mechanism replacing RNNs',
                                                    'Convolutional Neural Networks',
                                                    'Recursive Neural Networks',
                                                    'Long Short-Term Memory Units'
                                                ].map((option, index) => (
                                                    <button
                                                        key={index}
                                                        className="w-full p-3 text-left rounded-lg border border-purple-900/30 hover:bg-purple-900/30 transition-colors text-gray-300"
                                                    >
                                                        {option}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const additionalStyles = `
    @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    @keyframes slide-up {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
    }

    @keyframes scale-up {
        from { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
        to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    }

    .animate-fade-in {
        animation: fade-in 0.5s ease-out forwards;
    }

    .animate-slide-up {
        animation: slide-up 0.5s ease-out forwards;
    }

    .animate-scale-up {
        animation: scale-up 0.3s ease-out forwards;
    }
`;

const LandingPage = () => {
    const [isLoginOpen, setIsLoginOpen] = useState(false);  // Add this line
    const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user is already logged in
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                navigate('/search');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    React.useEffect(() => {
        const handleMouseMove = (e) => {
            // Add debouncing/throttling to reduce updates
            requestAnimationFrame(() => {
                setMousePosition({ x: e.clientX, y: e.clientY });
            });
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return (
        <div className="relative min-h-screen bg-black text-white overflow-hidden">
            <style>{additionalStyles}</style>
            {/* Add LoginDialog component */}
            <LoginDialog
                open={isLoginOpen}
                onOpenChange={setIsLoginOpen}
            />

            {/* Modify NavigationBar to pass the onGetStarted prop */}
            <NavigationBar onGetStarted={() => setIsLoginOpen(true)} />

            {/* In the hero section, update the Try Now button */}
            <button
                onClick={() => setIsLoginOpen(true)}
                className="group px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg...">
                Try Now
                <span className="inline-block transition-transform group-hover:translate-x-1 ml-2">→</span>
            </button>
            {/* Cursor spotlight */}
            <div
                className="fixed pointer-events-none inset-0 z-30 transition-transform duration-200"
                style={{
                    background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(147,51,234,0.07), transparent 40%)`,
                }}
            />
            <GridBackground />

            {/* Hero Section */}
            <header className="relative px-6 pt-32 pb-24 md:pt-40 md:pb-32">
                <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 via-transparent to-transparent" />
                <div className="max-w-6xl mx-auto text-center relative">
                    <div className="will-change-transform transform-gpu animate-fade-in-up opacity-0 translate-y-4">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            <span className="block">
                                Research Papers Made
                            </span>
                            <span className="block mt-2 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                                Interactive & Insightful
                            </span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-400 mb-8 max-w-3xl mx-auto">
                            Transform complex papers into knowledge graphs, key points, and interactive summaries.
                            Understand research through AI-powered insights and visual learning.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => setIsLoginOpen(true)}
                                className="group px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all hover:scale-105 duration-300 shadow-lg hover:shadow-purple-500/25">
                                Try Now
                                <span className="inline-block transition-transform group-hover:translate-x-1 ml-2">→</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Add the FeatureShowcase section here */}
            <FeatureShowcase />

            {/* Features Flow Section */}
            <section id="features" className="relative px-6 py-16">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent" />
                <div className="max-w-6xl mx-auto relative">
                    <h2 className="text-3xl font-bold text-center mb-12">
                        <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                            Why Papers with Graph?
                        </span>
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* AI-Powered Mindmaps */}
                        <div className="p-6 bg-purple-900/10 rounded-xl border border-purple-900/20 hover:border-purple-500/50 transition-all duration-300">
                            <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                                <Map className="w-6 h-6 text-purple-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-purple-400 mb-2">AI-Powered Mindmaps</h3>
                            <p className="text-gray-400">
                                Transform complex papers into intuitive knowledge graphs. Our AI automatically identifies key topics and generates visual mindmaps to help you understand paper structure.
                            </p>
                        </div>

                        {/* Interactive Q&A */}
                        <div className="p-6 bg-purple-900/10 rounded-xl border border-purple-900/20 hover:border-purple-500/50 transition-all duration-300">
                            <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                                <MessageSquare className="w-6 h-6 text-purple-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-purple-400 mb-2">Interactive Q&A</h3>
                            <p className="text-gray-400">
                                Ask questions about any paper and get instant, AI-generated answers. Deepen your understanding through natural conversation with our research assistant.
                            </p>
                        </div>

                        {/* Smart Note-Taking */}
                        <div className="p-6 bg-purple-900/10 rounded-xl border border-purple-900/20 hover:border-purple-500/50 transition-all duration-300">
                            <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                                <FileText className="w-6 h-6 text-purple-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-purple-400 mb-2">Smart Note-Taking</h3>
                            <p className="text-gray-400">
                                Take rich, Markdown-formatted notes as you read. Organize your thoughts and insights with our powerful note-taking system integrated directly with each paper.
                            </p>
                        </div>

                        {/* Enhanced Reading Experience */}
                        <div className="p-6 bg-purple-900/10 rounded-xl border border-purple-900/20 hover:border-purple-500/50 transition-all duration-300">
                            <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                                <Book className="w-6 h-6 text-purple-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-purple-400 mb-2">Enhanced Reading Experience</h3>
                            <p className="text-gray-400">
                                Read papers in our optimized viewer with a clean, distraction-free interface. Switch between PDF and enhanced HTML views for the best reading experience.
                            </p>
                        </div>

                        {/* Paper Library */}
                        <div className="p-6 bg-purple-900/10 rounded-xl border border-purple-900/20 hover:border-purple-500/50 transition-all duration-300">
                            <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                                <Layout className="w-6 h-6 text-purple-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-purple-400 mb-2">Paper Library</h3>
                            <p className="text-gray-400">
                                Keep all your research papers organized in one place. Access your papers instantly with our efficient library management system.
                            </p>
                        </div>

                        {/* Paper Search */}
                        <div className="p-6 bg-purple-900/10 rounded-xl border border-purple-900/20 hover:border-purple-500/50 transition-all duration-300">
                            <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                                <Search className="w-6 h-6 text-purple-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-purple-400 mb-2">Smart Search</h3>
                            <p className="text-gray-400">
                                Find papers quickly with our powerful search functionality. Search across your library or discover new research papers to add to your collection.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="relative px-6 py-16">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent" />
                <div className="max-w-6xl mx-auto relative">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">
                            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                                Simple, Transparent Pricing
                            </span>
                        </h2>
                        <p className="text-xl text-gray-400">Choose the plan that's right for you</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {[
                            {
                                id: 'free',
                                name: 'Free',
                                price: '$0',
                                features: [
                                    'First 3 papers',
                                    'Access to all mindmaps',
                                    'Unlimited AI chat',
                                ]
                            },
                            {
                                id: 'pro',
                                name: 'Pro',
                                price: '$10/month',
                                featured: true,
                                features: [
                                    'Everything in Free',
                                    '10 papers per month',
                                    'Priority support',
                                ]
                            },
                        ].map((plan) => (
                            <div
                                key={plan.id}
                                className={`relative p-8 rounded-2xl border ${plan.featured
                                    ? 'border-purple-500 bg-gradient-to-b from-purple-900/40 to-purple-900/20'
                                    : 'border-purple-900/30 bg-purple-900/10'
                                    } flex flex-col transform transition-all duration-200 hover:scale-105`}
                            >
                                <div className="mb-6">
                                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                                    <div className="flex items-baseline">
                                        <span className="text-4xl font-bold text-purple-400">{plan.price}</span>
                                    </div>
                                </div>
                                <ul className="flex-1 space-y-4 mb-8">
                                    {plan.features.map((feature, index) => (
                                        <li key={index} className="flex items-center text-gray-300">
                                            <span className="mr-3 text-purple-400">
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                            </span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => setIsLoginOpen(true)}
                                    className={`w-full py-4 text-base font-semibold transition-all duration-200 rounded-xl shadow-lg ${plan.featured
                                        ? 'bg-purple-500 hover:bg-purple-600 hover:shadow-purple-500/30 hover:-translate-y-0.5'
                                        : 'bg-purple-600/30 hover:bg-purple-600/50 hover:shadow-purple-600/20 hover:-translate-y-0.5'
                                        }`}
                                >
                                    Get Started
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="relative px-6 py-16">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 via-purple-800/5 to-transparent" />
                <div className="max-w-4xl mx-auto text-center relative">
                    <h2 className="text-3xl font-bold mb-6">
                        Ready to Transform Your Research Experience?
                    </h2>
                    <p className="text-xl text-gray-400 mb-8">
                        Join researchers worldwide who are using Papers with Graph to enhance their understanding
                        and collaboration.
                    </p>
                    <button
                        onClick={() => setIsLoginOpen(true)}
                        className="group px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all hover:scale-105 duration-300 shadow-lg">
                        Get Started Free
                        <span className="inline-block transition-transform group-hover:translate-x-1 ml-2">→</span>
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative px-6 py-8 border-t border-gray-800">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        <div>
                            <h3 className="text-white font-semibold mb-4">Legal</h3>
                            <ul className="space-y-2">
                                <li>
                                    <a href="/terms" className="text-gray-500 hover:text-purple-400 transition-colors">
                                        Terms of Service
                                    </a>
                                </li>
                                <li>
                                    <a href="/privacy" className="text-gray-500 hover:text-purple-400 transition-colors">
                                        Privacy Policy
                                    </a>
                                </li>
                                <li>
                                    <a href="/refund" className="text-gray-500 hover:text-purple-400 transition-colors">
                                        Refund Policy
                                    </a>
                                </li>
                            </ul>
                        </div>
                        {/* You can add more footer sections here */}
                    </div>
                    <div className="text-center text-gray-500">
                        <p>© 2025 Papers with Graph. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;